import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, utils} from '@api'
import {ChangeLogSingleItem, ISingleChangeItem, useVipLocalStore, VipLocalStore} from '@modules'
import {StoresInstance} from '@stores'
import * as css from './ChangeLogItems.css'

/// region Store

export class ChangeLogItemsStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  get item() {
    const {props} = this
    return props.item
  }

  get log() {
    const {item} = this
    return item._log
  }

  get innerHits() {
    const {item: {inner_hits: hits}, es} = this
    if (!hits) return []
    return Object.keys(hits).flatMap(key => {
      const {items} = es.unwrapHitsResult(hits[key])
      return items
    })
  }

  @computed get workspace() {
    const {log, workspaces: {workspaceByIndex}} = this
    return _.first(workspaceByIndex[log.index])
  }

  @computed get changes(): Array<ISingleChangeItem> {
    const {log, innerHits} = this
    return innerHits.length ? innerHits : log.changes
  }

  @computed get changesFlattenedWithFields() {
    const {changes, workspace: {catalog: {sectionById, fieldBySection, sectionValuesFor}}} = this

    return changes.reduce
    ((acc, change) => {
      const section = sectionById[change.field],
        fields = fieldBySection[change.field]
      if (!!section) {
        const values = sectionValuesFor(change.field),
          before = utils.isJson(change.diff?.before) ? JSON.parse(change.diff.before) : [],
          after = utils.isJson(change.diff?.after) ? JSON.parse(change.diff.after) : []
        if (values !== null) {
          return [
            ...acc,
            ..._.flatMap(values.map(({key}) => {
              const beforeObject = before.find(b => _.get(b, section.viewOptions.field) === key),
                afterObject = after.find(a => _.get(a, section.viewOptions.field) === key)
              return fields.map(field => {
                const f = field.field.replace(`${change.field}.`, ''),
                  a = _.get(afterObject, f),
                  b = _.get(beforeObject, f)
                if (a !== b) {
                  return {
                    changed: {
                      ...change,
                      id: `${change.id}_${key}_${f}`,
                      field: field.field,
                      diff: {
                        before: b,
                        after: a
                      }
                    },
                    label: section.label,
                    nested: key
                  }
                }
              }).filter(_.identity)
            }))
          ]
        }
      }

      return [
        ...acc,
        {changed: change}
      ]
    }, [])
  }

  @computed get animateIn() {
    return this.item.animateIn
  }
}

export const ChangeLogItemsStoreContext = React.createContext<ChangeLogItemsStore>(null)
export const useChangeLogItemsStore = () => React.useContext(ChangeLogItemsStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  item?: any
  index: number
}

export const ChangeLogItems = observer<MyProps>(function ChangeLogItems({className, ...props}) {
  const obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new ChangeLogItemsStore(stores, obsProps))

  if (!store.workspace) {
    return null
  }
  const {changesFlattenedWithFields} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <ChangeLogItemsStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}>
        {changesFlattenedWithFields.map((p, i) => <ChangeLogSingleItem {...p} index={i} key={i} />)}
      </div>
    </ChangeLogItemsStoreContext.Provider>)
})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/changelog/ChangeLogItems.tsx